import React, { useMemo } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  Paragraph,
  Profile,
  Block,
} from '@uncinc/react-drupal-core';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import {
  ArticleMetadata,
  BodyField,
  DynamicMediaItem,
  HighlightedProfiles,
  RelatedContentDefault as RelatedContent,
  Tag,
} from '../../../../components';
import { APP_OPTIONS } from '../../../../config/constants';
import { createSearchFacetUrl, createSearchUrlParam } from '../../../../helpers/createSearchUrl';
import { Breadcrumb, BreadcrumbState } from '../../../../types/uncinc_profile/helpers/breadcrumbs';
import { CustomNodeArticle } from '../../custom';

interface ArticlePageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<CustomNodeArticle>;
}

const ArticlePage: React.FC<ArticlePageProps> = ({ entity }) => {
  const { t } = useTranslation();

  const breadcrumbTopical = useSelector<BreadcrumbState, Breadcrumb | undefined>(
    (state) => {
      const breadcrumbs = state.getIn(['entity', 'breadcrumbs'], Immutable.List).toJS();

      if (breadcrumbs.length === 0) {
        return undefined;
      }

      // We want to get the crumb before the current page.
      return breadcrumbs[breadcrumbs.length - 2];
    },
  );

  const classes = classNames('NodeArticlePage');
  const article = entity.toJS();

  // Metadata.
  const nid = article.nid[0]?.value;
  const created = article.created[0].value;
  const readTime = article.node_read_time[0].value;

  // Basic fields.
  const title = article.title[0].value;
  const body = article.body[0]?.processed;
  const media = article.field_media_item?.[0];
  const paragraphs = article.field_paragraphs;

  // Reference fields.
  const articleType = article.field_article_type[0]?.entity.name[0].value;
  const theme = article.field_themes[0];
  const authorIds = article.field_author.map((author) => author.target_id);

  // Custom fields.
  const ctaBlockEntity = entity.getIn(['field_cta_block', 0, 'entity'], Immutable.Map());
  const contactFormBlockEntity = entity.getIn(['field_contact_form_block', 0, 'entity'], Immutable.Map());

  const createArticleSearchLink = useMemo(() => {
    if (!breadcrumbTopical || !breadcrumbTopical.url) return '';

    const { searchkitIndexTopical } = APP_OPTIONS.parameters;

    const facetType = 'refinementList';
    const base = createSearchFacetUrl({
      pathname: breadcrumbTopical.url,
      index: searchkitIndexTopical,
      facetType,
      attribute: 'article_type',
      value: articleType!,
    });
    return `${base}&${createSearchUrlParam(searchkitIndexTopical, [facetType, 'type', 0], 'article')}`;
  }, [breadcrumbTopical, articleType]);

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <Container>
        <div className="row">
          <div className="col-12 col-lg-7 mt-6">
            {(theme || articleType) && (
              <div className="tags NodeArticlePage__tags">
                {theme && (
                  <Tag
                    type="theme"
                    name={theme.entity.name[0].value}
                    stretchClickableArea
                    target="_self"
                    link={theme.url}
                  />
                )}

                {articleType && (
                  <Tag
                    type="article-type"
                    name={articleType}
                    target="_self"
                    link={createArticleSearchLink}
                  />
                )}
              </div>
            )}

            <div className="NodeArticlePage__content">
              <h1 itemProp="name">{title}</h1>

              {body && (
                <div className="mb-6">
                  <BodyField html={body} />
                </div>
              )}

              <ArticleMetadata>
                <ArticleMetadata.ReadTime readTime={Number(readTime)} />
                <ArticleMetadata.PublicationDate publicationDate={new Date(created)} />
              </ArticleMetadata>
            </div>

            {media && (
              <div className="content-section">
                <div className="NodeArticlePage__image">
                  <DynamicMediaItem media={media} imageAspectRatio="2_1" aboveTheFold />
                </div>
              </div>
            )}

            {paragraphs.length > 0 && (
              <div className="content-section">
                <div className="NodeArticlePage__paragraphs">
                  {paragraphs.map((paragraph) => {
                    const id = paragraph.target_id;
                    const language = paragraph.entity.langcode[0].value;

                    return (
                      <Paragraph
                        key={`paragraph:${id}:${language}`}
                        id={id}
                        entity={Immutable.fromJS(paragraph.entity)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {(authorIds.length > 0 || ctaBlockEntity) && (
            <div className="col-lg-5 col-xl-4 offset-xl-1 mt-lg-6">
              {authorIds.length > 0 && (
                <div className="sidebar-section">
                  <HighlightedProfiles heading={t('components.default_profile_teaser.title')}>
                    <p>{t('components.default_profile_teaser.subtitle')}</p>
                    {authorIds.map((authorId, idx) => (
                      <Profile key={`profile-${String(authorId)}`} id={authorId} className={idx !== 0 && 'mt-12'} />
                    ))}
                  </HighlightedProfiles>
                </div>
              )}

              {ctaBlockEntity && (
                <div className="sidebar-section">
                  <Block entity={ctaBlockEntity} />
                </div>
              )}
            </div>
          )}
        </div>

        <RelatedContent
          type="by_theme"
          args={[nid, article.field_themes.map((term) => term.target_id)]}
        />

        {contactFormBlockEntity && (
          <Block entity={contactFormBlockEntity} />
        )}
      </Container>
    </article>
  );
};

export default ArticlePage;
